// === App Entry Point (ES Module) ===
import './css/main.css';

import store, { MONTH_OVERRIDES_KEY } from './state/store.js';
import { getElements } from './utils/dom.js';
import { getItem } from './services/storage-service.js';
import { loadAdvisories } from './services/data-service.js';
import { setupTimeline } from './components/timeline.js';
import { updateHeaderDisplay, setupScrollBehavior } from './components/header.js';
import { renderCards } from './components/card-renderer.js';
import { setView } from './components/view-switcher.js';
import { initOnboarding, renderHome } from './components/onboarding.js';
import { setupSearch } from './components/search.js';
import { setupInfoModals } from './components/info-modal.js';
import { setupPwaInstall, triggerInstall } from './components/pwa-install.js';
import { setupCardModal } from './components/card-modal.js';
import { getShiftedMonths } from './services/filter-service.js';
import { exportCardIcs } from './utils/ics.js';
import { shareItem } from './utils/share.js';
import { setupFeedbackModal } from './utils/feedback.js';
import { showToast } from './utils/toast.js';
import { loadCompletions, toggleDone } from './services/completion-service.js';
import { initNav } from './utils/nav.js';

async function init() {
  // Initialise the Back-button navigation stack before anything pushes a screen.
  initNav();

  // Load completions (per-year done state, migrates the old format)
  loadCompletions();

  // Load per-task month overrides
  const savedOverrides = getItem(MONTH_OVERRIDES_KEY);
  if (savedOverrides && typeof savedOverrides === 'object') store.monthOverrides = savedOverrides;

  // Onboarding & profile
  initOnboarding();

  // Card detail modal — set up before any cards render so app.openCard exists
  setupCardModal();

  // Timeline
  setupTimeline();

  // Load data
  const loaded = await loadAdvisories();
  if (loaded) {
    updateHeaderDisplay();
    renderCards();
    // Refresh the Home overview now that advisories are loaded (progress count).
    renderHome();
  }

  // Event listeners
  const els = getElements();

  // Month pill clicks (event delegation)
  els.monthPills.addEventListener('click', (e) => {
    const pill = e.target.closest('.month-pill');
    if (!pill) return;
    store.currentMonth = parseInt(pill.dataset.month);
    updateHeaderDisplay();
    renderCards();
  });

  els.btnGrid.addEventListener('click', () => setView('grid'));
  els.btnList.addEventListener('click', () => setView('list'));

  // Search
  setupSearch({
    onNavigate(item) {
      const shifted = getShiftedMonths(item);
      if (shifted && shifted.length) {
        const current = store.currentMonth + 1;
        const target = shifted.includes(current) ? current : shifted[0];
        store.currentMonth = target - 1;
      }
      updateHeaderDisplay();
      renderCards();
      setTimeout(() => {
        const flip = document.getElementById(`flip-${item.id}`);
        const target = flip ? flip.parentElement : document.getElementById(`list-item-${item.id}`);
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Wait for scroll to settle before starting the flash, so it's not visually masked
        setTimeout(() => {
          target.classList.add('search-target-flash');
          setTimeout(() => target.classList.remove('search-target-flash'), 5600);
        }, 450);
      }, 50);
    }
  });

  // Scroll behavior
  setupScrollBehavior();

  // Footer info modals (Privacy / Imprint / About)
  setupInfoModals();

  // Footer feedback modal (in-app free-text → AHI Google Form)
  setupFeedbackModal();

  // Capture the PWA install prompt as early as possible
  setupPwaInstall();
}

// Global API for onclick handlers in dynamically generated HTML
window.app = {
  toggle(id, e) {
    if (e) e.stopPropagation();
    toggleDone(id);
    renderCards();
  },

  exportCard(id) {
    const item = store.seed.find(i => i.id === id);
    if (item) exportCardIcs(item);
  },

  shareCard(id) {
    const item = store.seed.find(i => i.id === id);
    if (!item) return;
    shareItem(item).then(result => {
      if (result === 'copied') showToast('Copied to clipboard');
      else if (result === 'mailto') showToast('Opening email…');
    });
  },

  installApp() {
    triggerInstall();
  }
};

init();

// Register Service Worker for PWA / offline support.
// Auto-reload the page when a new SW takes control so users always see the
// latest deploy without needing a hard refresh.
if ('serviceWorker' in navigator) {
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(reg => {
      // Check for updates whenever the user returns to the tab
      const checkForUpdate = () => reg.update().catch(() => {});
      window.addEventListener('focus', checkForUpdate);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdate();
      });
    }).catch(() => {});
  });
}
